/**
 * Dev-only stress test: real pathfinding routes + fleet rendering.
 * Fully loads GPU/CPU for multiplayer limit testing.
 */
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { WorldInfo } from '../config/WorldConfig';
import type { TravelConfig } from '../config/TravelConfig';
import type { FleetConfig } from '../config/FleetConfig';
import type { Camera } from '../renderer/Camera';
import type { RenderPipeline } from '../renderer/RenderPipeline';
import { StressFleetRenderer, type FleetPosition } from '../objects/StressFleetRenderer';
import {
  type StressRoute, MAX_ROUTE_COUNT, generateStressRoutes,
} from './TravelStressTestSetup';
export { MAX_SHIPS_PER_FLEET } from './TravelStressTestConst';

const DEFAULT_ROUTE_COUNT = 50;

export interface TravelStressTestState {
  active: boolean;
  routeCount: number;
  visibleFleets: number;
  loading: boolean;
}

export class TravelStressTest implements GameModule {
  readonly name = 'TravelStressTest';
  readonly state: TravelStressTestState = { active: false, routeCount: 0, visibleFleets: 0, loading: false };

  private _engine!: Engine;
  private _camera: Camera;
  private _pipeline: RenderPipeline;
  private _worldInfo: WorldInfo;
  private _travelConfig: TravelConfig;
  private _fleetConfig: FleetConfig;
  private _sunKeysProvider: (() => ReadonlySet<number>) | null = null;
  private _routes: StressRoute[] = [];
  private _fleetRenderer: StressFleetRenderer | null = null;
  private readonly _positions: FleetPosition[] = [];
  private _positionCount = 0;
  private _seed = 12345;
  private _showLines = false;

  constructor(
    camera: Camera, pipeline: RenderPipeline, worldInfo: WorldInfo,
    travelConfig: TravelConfig, fleetConfig: FleetConfig,
    sunKeysProvider?: (() => ReadonlySet<number>) | null,
  ) {
    this._camera = camera;
    this._pipeline = pipeline;
    this._worldInfo = worldInfo;
    this._travelConfig = travelConfig;
    this._fleetConfig = fleetConfig;
    this._sunKeysProvider = sunKeysProvider ?? null;
    for (let i = 0; i < MAX_ROUTE_COUNT; i++) {
      this._positions.push({
        state: null! as import('./FleetState').FleetState,
        x: 0, y: 0, z: 0, dirX: 0, dirZ: 0, turnRate: 0,
      });
    }
  }

  init(engine: Engine): void {
    this._engine = engine;
    document.addEventListener('keydown', this._onKey, { capture: true });
  }

  async start(count: number): Promise<number> {
    if (this.state.loading || this.state.active) return this.state.routeCount;
    this.state.loading = true;
    try {
      return await this._doStart(count);
    } catch (e) {
      console.error('[StressTest] Fehler:', e);
      this.stop();
      return 0;
    } finally {
      this.state.loading = false;
    }
  }

  private async _doStart(count: number): Promise<number> {
    const sunKeys: number[] = [];
    if (this._sunKeysProvider) {
      for (const k of this._sunKeysProvider()) sunKeys.push(k);
    }

    const hexRadius = this._worldInfo.config.hexRadius;
    const fleetRenderer = new StressFleetRenderer(
      this._engine.gl, this._fleetConfig.formationSpacing, hexRadius,
    );
    try { await fleetRenderer.loadModels(); }
    catch (e) { console.warn('[StressTest] Fleet models not found:', e); }

    this._fleetRenderer = fleetRenderer;
    this._pipeline.addRenderable(fleetRenderer);
    this.state.active = true;

    const routes = await generateStressRoutes(
      count, this._engine.gl, this._worldInfo, this._travelConfig,
      this._fleetConfig, this._pipeline, sunKeys, this._showLines,
      { seed: this._seed },
    );
    this._routes = routes;
    this.state.routeCount = routes.length;

    if (routes.length > 0) {
      console.log('[StressTest] Fertig:', routes.length, 'Routen');
    }
    return routes.length;
  }

  getRouteCenter(index: number): { x: number; z: number } | null {
    if (index < 0 || index >= this._routes.length) return null;
    const r = this._routes[index];
    return { x: r.fleetState.centerX, z: r.fleetState.centerZ };
  }

  setShowLines(v: boolean): void {
    this._showLines = v;
    for (let i = 0; i < this._routes.length; i++) this._routes[i].line.setSuppressed(!v);
  }

  get showLines(): boolean { return this._showLines; }

  getCenterOfAllRoutes(): { x: number; z: number } | null {
    if (this._routes.length === 0) return null;
    let sx = 0, sz = 0;
    for (let i = 0; i < this._routes.length; i++) {
      sx += this._routes[i].fleetState.centerX;
      sz += this._routes[i].fleetState.centerZ;
    }
    return { x: sx / this._routes.length, z: sz / this._routes.length };
  }

  stop(): void {
    for (let i = 0; i < this._routes.length; i++) {
      this._routes[i].line.dispose();
      this._pipeline.removeRenderable(this._routes[i].line);
    }
    this._routes.length = 0;
    if (this._fleetRenderer) {
      this._fleetRenderer.dispose();
      this._pipeline.removeRenderable(this._fleetRenderer);
      this._fleetRenderer = null;
    }
    this.state.active = false;
    this.state.routeCount = 0;
  }

  dispose(): void {
    this.stop();
    document.removeEventListener('keydown', this._onKey, { capture: true });
  }

  update(dt: number): void {
    if (!this.state.active || !this._fleetRenderer) return;
    const fh = this._travelConfig.flightHeight;
    this._positionCount = 0;
    for (let i = 0; i < this._routes.length; i++) {
      const r = this._routes[i];
      r.line.advanceTime(dt);
      const res = r.animator.update(dt);
      r.fleetState.update(res.worldX, fh, res.worldZ, res.dirX, res.dirZ, res.turnRate);
      const idx = this._positionCount++;
      const p = this._positions[idx];
      p.state = r.fleetState;
      p.x = res.worldX; p.y = fh; p.z = res.worldZ;
      p.dirX = res.dirX; p.dirZ = res.dirZ; p.turnRate = res.turnRate;
      if (res.finished) {
        r.animator.start(r.route, null);
        r.fleetState.active = true;
      }
    }
    this._fleetRenderer.update(dt);
    this._fleetRenderer.updateFormation(this._positions, this._camera, this._positionCount);
    this.state.visibleFleets = this._fleetRenderer.visibleFleets;
  }

  private _onKey = (e: KeyboardEvent): void => {
    if (!e.ctrlKey || !e.shiftKey || e.key !== 'S') return;
    e.preventDefault();
    if (this.state.active) this.stop();
    else if (!this.state.loading) this.start(DEFAULT_ROUTE_COUNT);
  };
}
